import { DEFAULT_LOCALE, type NoteName, type SupportedLocale } from '@cavaquinho/shared';

/**
 * Note-name localisation (FR-066, SC-016, research D-005).
 *
 * The Latin letter is the canonical written form: it is what the note pool
 * stores, what an attempt records, and what travels on every route in the API
 * contract. Do-Si is PRESENTATION AND NOTHING ELSE, which is why this module
 * lives in the Web app and takes a locale rather than the API returning
 * different letters to different learners.
 *
 * The consequence worth stating: two attempts recorded either side of a locale
 * change are identical rows. Changing locale alters no attempt, no completion,
 * no session, no history and no Exercise configuration (FR-075).
 *
 * `pt-BR` is the default (FR-066). The audience is Brazilian, and the movable
 * -do solfège they read music in is Do Re Mi, not letters.
 */

const PT_BR: Record<NoteName, string> = {
  C: 'Dó',
  D: 'Ré',
  E: 'Mi',
  F: 'Fá',
  G: 'Sol',
  A: 'Lá',
  B: 'Si',
};

export function noteLabel(note: NoteName, locale: SupportedLocale = DEFAULT_LOCALE): string {
  return locale === 'pt-BR' ? PT_BR[note] : note;
}

/**
 * The same name for a screen reader.
 *
 * Identical to the visible label today, and deliberately its own function: an
 * announcement is a sentence a reader hears once, and if a future locale needs
 * "Do" spelled differently for a speech synthesiser than for the eye, this is
 * where that divergence belongs rather than inside a component.
 */
export function noteAnnouncement(note: NoteName, locale: SupportedLocale = DEFAULT_LOCALE): string {
  return noteLabel(note, locale);
}
